import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { IconButton, ButtonBase } from "@mui/material";
import * as React from "react";

import {
  TimeDisplayMode,
  useTimeDisplayMode,
} from "@/shared/components/visualization/WorkspaceCtx";
import { usePopupState } from "@/shared/hooks";
import { actions, useVizDispatch } from "@/shared/state/visualization";

import {
  SetCombinatorAction,
  type ColumnFiltersAction,
} from "../columnFilters/actions";
import type { ColumnDefinition } from "../columnFilters/columnDefinition";
import { ColumnFilterPopover } from "../columnFilters/ColumnFilterPopover";
import type { FilterStageColorVars } from "../columnFilters/filterStageColors";
import { buildPendingCommitActions } from "../columnFilters/pendingCommit";
import {
  type FilterStage,
  type ColumnFiltersState,
  type StageCombinator,
} from "../columnFilters/state";
import { useColumnFilterActions } from "../columnFilters/useColumnFilterActions";
import panelStyles from "../LogPanel.module.css";

import headerStyles from "./TableHeader.module.css";

function getTimeColumnHeader(mode: TimeDisplayMode): string {
  switch (mode) {
    case TimeDisplayMode.Elapsed:
    case TimeDisplayMode.Log:
      return "Time (s)";
    case TimeDisplayMode.ElapsedTime:
      return "Time (MM:SS)";
    case TimeDisplayMode.Local:
    case TimeDisplayMode.UTC:
      return "Time";
  }
}

interface LogTableHeaderProps {
  columnDefinitions: ColumnDefinition[];
  columnFilters: ColumnFiltersState;
  columnFiltersDispatch: React.Dispatch<ColumnFiltersAction>;
  colorMap: Map<string, number>;
  stageColors: FilterStageColorVars[];
  canFilter: boolean;
  panelId: string;
}

export function TableHeader({
  columnDefinitions,
  panelId,
  canFilter,
  columnFilters,
  columnFiltersDispatch,
  colorMap,
  stageColors,
}: LogTableHeaderProps) {
  const [timeDisplayMode] = useTimeDisplayMode();

  return (
    <div className={`${panelStyles.rowHeader} ${headerStyles.headerRow}`}>
      <h6 className={headerStyles.header}>
        {getTimeColumnHeader(timeDisplayMode)}
      </h6>
      {columnDefinitions.map((column) => (
        <ColumnHeader
          panelId={panelId}
          column={column}
          canFilter={canFilter}
          key={column.messagePathId}
          columnFilters={columnFilters}
          columnFiltersDispatch={columnFiltersDispatch}
          colorMap={colorMap}
          stageColors={stageColors}
          timeDisplayMode={timeDisplayMode}
        />
      ))}
    </div>
  );
}

function ColumnHeader({
  panelId,
  column,
  canFilter,
  columnFilters,
  columnFiltersDispatch,
  colorMap,
  stageColors,
  timeDisplayMode,
}: {
  panelId: string;
  column: ColumnDefinition;
  canFilter: boolean;
  columnFilters: ColumnFiltersState;
  columnFiltersDispatch: React.Dispatch<ColumnFiltersAction>;
  colorMap: Map<string, number>;
  stageColors: FilterStageColorVars[];
  timeDisplayMode: TimeDisplayMode;
}) {
  const dispatch = useVizDispatch();
  const popup = usePopupState();
  const existingFilter = columnFilters.filters[column.messagePathId];

  // Pending stages survive popover close/reopen when no filter exists yet.
  const [pendingStages, setPendingStages] = React.useState<FilterStage[]>([
    { value: "", condition: "contains" },
  ]);

  // Reset pending stages only when transitioning from no-filter to has-filter.
  const hadFilterRef = React.useRef(existingFilter !== undefined);
  React.useEffect(() => {
    const hasFilter = existingFilter !== undefined;
    if (hasFilter && !hadFilterRef.current) {
      setPendingStages([{ value: "", condition: "contains" }]);
    }
    hadFilterRef.current = hasFilter;
  }, [existingFilter]);

  const currentStages = existingFilter?.stages ?? pendingStages;
  const currentCombinator = existingFilter?.combinator ?? "or";

  const filterActions = useColumnFilterActions(
    column.messagePathId,
    columnFiltersDispatch,
  );

  const commitPendingToReducer = (nextStages: FilterStage[]) => {
    // When no filter exists yet, flush all pending stages (including earlier
    // ones whose values are still empty) to the reducer as a sequence of
    // SetFilterActions, so the column filter actually takes effect as soon as
    // any stage becomes meaningful. See buildPendingCommitActions for rules.
    const actions = buildPendingCommitActions(column.messagePathId, nextStages);
    if (actions === null) {
      return;
    }
    for (const action of actions) {
      columnFiltersDispatch(action);
    }
  };

  const onStageChange = (stageIndex: number, stage: FilterStage) => {
    if (existingFilter) {
      filterActions.onStageChange(stageIndex, stage);
      return;
    }
    const nextStages = [...pendingStages];
    nextStages[stageIndex] = stage;
    setPendingStages(nextStages);
    commitPendingToReducer(nextStages);
  };

  const onStageAdd = (stage: FilterStage) => {
    if (existingFilter) {
      filterActions.onStageAdd(stage);
      return;
    }
    const nextStages = [...pendingStages, stage];
    setPendingStages(nextStages);
    commitPendingToReducer(nextStages);
  };

  const onStageDelete = (stageIndex: number) => {
    if (!existingFilter) {
      setPendingStages((prev) => prev.filter((_, i) => i !== stageIndex));
      return;
    }
    if (existingFilter.stages.length === 1) {
      // Deleting the last stage removes the filter from the reducer entirely.
      // Reset pendingStages so the popover starts fresh if reopened.
      setPendingStages([{ value: "", condition: "contains" }]);
    }
    filterActions.onStageDelete(stageIndex);
  };

  const onCombinatorChange = (combinator: StageCombinator) => {
    columnFiltersDispatch(
      new SetCombinatorAction({
        columnId: column.messagePathId,
        combinator,
      }),
    );
  };

  const headerRef = React.useRef<HTMLSpanElement>(null);

  return (
    <h6 className={`ph-no-capture ${headerStyles.header}`}>
      {canFilter ? (
        <ButtonBase
          ref={headerRef}
          className={headerStyles.headerButton}
          component="span"
          title="Search column"
          onClick={() => {
            if (!popup.isOpen) {
              popup.open({ anchorEl: headerRef.current });
            } else {
              popup.close();
            }
          }}
        >
          {column.name}
          <IconButton
            disableRipple
            aria-label={`Filter ${column.name}`}
            size="small"
            className={headerStyles.headerIconButton}
          >
            <SearchIcon fontSize="small" className={headerStyles.headerIcon} />
          </IconButton>
        </ButtonBase>
      ) : (
        <span className={headerStyles.headerText}>{column.name}</span>
      )}
      <ColumnFilterPopover
        popup={popup}
        columnName={column.name}
        columnId={column.messagePathId}
        colorMap={colorMap}
        stageColors={stageColors}
        stages={currentStages}
        dataType={column.dataType}
        timeDisplayMode={timeDisplayMode}
        combinator={currentCombinator}
        onStageChange={onStageChange}
        onStageAdd={onStageAdd}
        onStageDelete={onStageDelete}
        onCombinatorChange={onCombinatorChange}
      />
      <IconButton
        aria-label="remove column"
        onClick={() => {
          dispatch(
            actions.removeMessagePathFromLogPanel(
              panelId,
              column.messagePathId,
            ),
          );
        }}
        size="small"
        className={headerStyles.headerIconButton}
        title="Remove column"
      >
        <CloseIcon fontSize="small" className={headerStyles.headerIcon} />
      </IconButton>
    </h6>
  );
}
